import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';
import useTreatmentPlanV3DischargePageState from './useTreatmentPlanV3DischargePageState';

import type { AppDispatch } from '../../../../../../store';
import type {
  DischargeCriteriaPayload,
  DischargeReasonPayload,
} from '../../../../../../store/treatmentPlanV3';
import type {
  TreatmentPlanV3DischargeDependencies,
  UseTreatmentPlanV3DischargeResult,
} from './types';

export default function useTreatmentPlanV3Discharge() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const pageState = useTreatmentPlanV3DischargePageState();

  return useTreatmentPlanV3DischargeWithDependencies({
    actions: treatmentPlanV3Actions,
    dispatch,
    getAccessTokenSilently,
    treatmentPlanId,
    ...pageState,
  });
}

export function useTreatmentPlanV3DischargeWithDependencies({
  actions,
  dispatch,
  getAccessTokenSilently,
  treatmentPlanId,
  ...pageState
}: TreatmentPlanV3DischargeDependencies): UseTreatmentPlanV3DischargeResult {
  const updateCriteria = useCallback(
    async (data: DischargeCriteriaPayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3DischargeCriteria({
          treatmentPlanId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3DischargeCriteria.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const createReason = useCallback(
    async (data: DischargeReasonPayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.createTreatmentPlanV3DischargeReason({
          treatmentPlanId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.createTreatmentPlanV3DischargeReason.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const deleteReason = useCallback(
    async (reasonId: number) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.deleteTreatmentPlanV3DischargeReason({
          treatmentPlanId,
          reasonId,
          getAccessTokenSilently,
        }),
      );
      return actions.deleteTreatmentPlanV3DischargeReason.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  return {
    ...pageState,
    updateCriteria,
    createReason,
    deleteReason,
  };
}
